import React, { useState, useEffect } from 'react';
import { apiCall } from '../../utils/apiUtils';
import { showToast } from '../../utils/toastUtils';

const TicketGenerationCart = ({ isOpen, onClose, selectedOrders, onInvoiceGenerated }) => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceNote, setInvoiceNote] = useState('');

  useEffect(() => {
    if (isOpen && selectedOrders.length > 0) {
      initializeCart();
    }
  }, [isOpen, selectedOrders]);

  const initializeCart = () => {
    const initialItems = selectedOrders.map(order => {
      const unpaid_amount = (order.total_stamp_duty || 0) - (order.paid_stamp_duty || 0);
      const max_quantity = Math.floor(unpaid_amount / order.denomination);

      return {
        order_id: order.id,
        order_number: order.order_number,
        quantity: order.quantity,
        denomination: order.denomination,
        total_stamp_duty: order.total_stamp_duty,
        paid_stamp_duty: order.paid_stamp_duty || 0,
        unpaid_amount: unpaid_amount,
        quantity_to_pay: Math.min(order.quantity, max_quantity),
        client_name: order.client_name,
        first_party_name: (order.first_party && order.first_party.name) || order.first_party_name || 'N/A',
        vendor_name: order.vendor_name || order.vendor?.name || order.first_party?.name || 'N/A'
      };
    });
    setCartItems(initialItems);
    setInvoiceNote('');
  };

  const updateQuantity = (orderId, newQuantity) => {
    setCartItems(items => items.map(item => {
      if (item.order_id === orderId) {
        const maxQuantity = Math.floor(item.unpaid_amount / item.denomination);
        const validQuantity = Math.min(Math.max(0, newQuantity), maxQuantity);
        return {
          ...item,
          quantity_to_pay: validQuantity
        };
      }
      return item;
    }));
  };

  const removeFromCart = (orderId) => {
    setCartItems(items => items.filter(item => item.order_id !== orderId));
  };

  const getTotalAmount = () => {
    return cartItems.reduce((total, item) => total + (item.quantity_to_pay * item.denomination), 0);
  };

  // Removed per-item notes; using single invoice note instead

  const handlePreviewInvoice = async () => {
    if (cartItems.length === 0) {
      showToast.error('Cart is empty');
      return;
    }

    const validItems = cartItems.filter(item => item.quantity_to_pay > 0);
    if (validItems.length === 0) {
      showToast.error('No items with valid quantities to ticket');
      return;
    }

    try {
      setLoading(true);
      const invoiceData = {
        payer_email: process.env.REACT_APP_PAYER_EMAILS || "ravi@matrica.in",
        invoice_items: validItems.map(item => ({
          order_id: item.order_id,
          quantity_paid: item.quantity_to_pay,
          amount_paid: item.quantity_to_pay * item.denomination
        })),
        notes: invoiceNote || null
      };

      const response = await apiCall('/api/invoices/preview', {
        method: 'POST',
        body: JSON.stringify(invoiceData)
      });

      setPreviewData(response.preview || response);
      setShowPreview(true);
    } catch (error) {
      console.error('Error previewing purchase order:', error);
      showToast.error('Failed to preview purchase order');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInvoice = async () => {
    try {
      setGeneratingInvoice(true);
      const invoiceData = {
        payer_email: process.env.REACT_APP_PAYER_EMAILS || "ravi@matrica.in",
        invoice_items: cartItems.filter(item => item.quantity_to_pay > 0).map(item => ({
          order_id: item.order_id,
          quantity_paid: item.quantity_to_pay,
          amount_paid: item.quantity_to_pay * item.denomination
        })),
        notes: invoiceNote || null
      };

      const response = await apiCall('/api/invoices/generate', {
        method: 'POST',
        body: JSON.stringify(invoiceData)
      });

      showToast.success(`Purchase Order ${response.invoice_number} generated successfully!`);
      onInvoiceGenerated();
      onClose();
    } catch (error) {
      console.error('Error generating purchase order:', error);
      showToast.error('Failed to generate purchase order');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto mx-4">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Ticket Generation Cart</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {cartItems.length === 0 ? (
            <div className="text-center py-12">
              <svg className="w-16 h-16 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 11-4 0v-6m-6 0h2.5" />
              </svg>
              <p className="text-lg text-gray-500">Cart is empty</p>
            </div>
          ) : (
            <>
              {/* Cart Items Table */}
              <div className="overflow-x-auto mb-6">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Order ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Client Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        First Party Name
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Quantity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Denomination
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Total Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Paid Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Unpaid Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quantity to Pay
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {cartItems.map((item) => (
                      <tr key={item.order_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {item.order_number}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.client_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.first_party_name || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {item.quantity}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(item.denomination)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(item.total_stamp_duty)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(item.paid_stamp_duty)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(item.unpaid_amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="number"
                            min="0"
                            max={Math.floor(item.unpaid_amount / item.denomination)}
                            value={item.quantity_to_pay}
                            onChange={(e) => updateQuantity(item.order_id, parseInt(e.target.value) || 0)}
                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                          <div className="text-xs text-gray-500 mt-1">
                            Max: {Math.floor(item.unpaid_amount / item.denomination)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(item.quantity_to_pay * item.denomination)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => removeFromCart(item.order_id)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Remove from cart"
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary and Actions */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="text-lg font-semibold text-gray-900">
                    Total Payment Amount: {formatCurrency(getTotalAmount())}
                  </div>
                  <div className="text-sm text-gray-600">
                    {cartItems.filter(item => item.quantity_to_pay > 0).length} items
                  </div>
                </div>

                {/* Single Invoice Note */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Invoice Note</label>
                  <input
                    type="text"
                    value={invoiceNote}
                    onChange={(e) => setInvoiceNote(e.target.value)}
                    maxLength={200}
                    placeholder="Add a note for the entire invoice"
                    className="w-full md:w-[32rem] px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <div className="text-xs text-gray-500 mt-1">{invoiceNote.length}/200 characters</div>
                </div>

                <div className="flex justify-end space-x-4">
                  <button
                    onClick={onClose}
                    className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePreviewInvoice}
                    disabled={loading || cartItems.filter(item => item.quantity_to_pay > 0).length === 0}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {loading ? 'Loading...' : 'Preview Invoice'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Invoice Preview Modal */}
      {showPreview && previewData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-60">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto mx-4">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-900">Purchase Order Preview</h3>
                <button
                  onClick={() => setShowPreview(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h4 className="text-lg font-semibold text-gray-900 mb-4">Purchase Order Details</h4>
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <span className="font-medium text-gray-700">Purchase Order ID:</span>
                    <span className="ml-2">{previewData.invoice_number}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Total Amount:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(previewData.total_amount)}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Accountant:</span>
                    <span className="ml-2">{previewData.accountant_name}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Payer Email:</span>
                    <span className="ml-2">{previewData.payer_email}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Date:</span>
                    <span className="ml-2">{new Date(previewData.created_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</span>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Vendor:</span>
                    <span className="ml-2">{previewData.vendor_name || 'N/A'}</span>
                  </div>
                  <div className="col-span-2">
                    <span className="font-medium text-gray-700">Notes:</span>
                    <span className="ml-2">{previewData.notes || invoiceNote || '—'}</span>
                  </div>
                </div>

                <h5 className="font-medium text-gray-900 mb-2">Order Details</h5>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Order ID
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Client Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          First Party Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Quantity Paid
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Denomination
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {previewData.order_details?.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {item.order_id}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {item.client_name || 'N/A'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {item.first_party_name || 'N/A'}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {item.quantity_paid}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.denomination)}
                          </td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                            {formatCurrency(item.amount_paid)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  onClick={() => setShowPreview(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Back to Cart
                </button>
                <button
                  onClick={handleGenerateInvoice}
                  disabled={generatingInvoice}
                  className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketGenerationCart;